export const STORAGE_ITEM_NAME = 'skin_concierge_crm_email'
export const COOKIE_KEY_EMAIL = 'buly_email'

/**
 *
 * @param {string} query
 * @param {boolean} isJapanese
 * @returns
 */
export async function fetchShopifyData(query, isJapanese = false) {
	const shop = isJapanese ? 'buly1803-japan' : 'buly1803-france'

	// Public keys.
	const accessToken = isJapanese
		? 'f64cf4107d42ae04ecc347a74691a9fa'
		: '2b3619d860f6c2b07427bda1bac1c702'

	const url = `https://${shop}.myshopify.com/api/2024-01/graphql.json`
	const headers = {
		'Content-Type': 'application/json',
		'X-Shopify-Storefront-Access-Token': accessToken
	}
	const body = JSON.stringify({
		query: query
	})

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: headers,
			body: body
		})

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const data = await response.json()
		return data
	} catch (error) {
		this.$ga.send(this.$constants.ANALYTICS_EVENTS.PRODUCT_FINDER_ERROR, {
			product_finder_name: this.$constants.PRODUCT_FINDER_NAME,
			error_type: 'fetch_shopify_data'
		})
		console.error('Could not fetch data from Shopify:', error)
	}
}

/**
 * Processes the skin concierge profile payload.
 *
 * @param {Object} data - The data object containing profile information.
 * @param {string} data.email - The email address of the user.
 * @param {string} [data.name] - The name of the user.
 * @param {string} [data.skinConcern] - The skin concern of the user (optional).
 * @param {"fr" | "en" | "jp"} data.language - The language preference of the user.
 * @returns {void} - The function does not return a value.
 */
export async function saveProfile(data) {
	try {
		const payload = {
			action: 'save_list',
			...data
		}
		const response = await fetch(process.env.VUE_APP_FUNCTIONS_API, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(payload)
		})

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const result = await response.json()
		if (!result.success) {
			throw new Error('Failed to get success.')
		}

		return true
	} catch (error) {
		this.$ga.send(this.$constants.ANALYTICS_EVENTS.PRODUCT_FINDER_ERROR, {
			product_finder_name: this.$constants.PRODUCT_FINDER_NAME,
			error_type: 'save_profile'
		})
		// @notes: track error
		console.error('Error:', error)
		throw error
	}
}

/**
 * Processes the skin concierge profile payload.
 *
 * @param {string} email - The email address of the user.
 * @param {string} [skinConcern] - The skin concern of the user (optional).
 * @param {"fr" | "en" | "jp"} language - The language preference of the user.
 * @param {Array<ProductsCRM>} productsCRM - An array of product information objects.
 * @typedef {Object} ProductsCRM
 * @property {string} img - The image URL of the product.
 * @property {string} productTitle - The title of the product.
 * @property {string} productVariantTitle - The title of the product variant.
 * @property {string} priceAmount - The price amount of the product.
 * @property {string} priceCurrency - The currency of the product price.
 * @property {string} description - The description of the product.
 * @property {number} id - The ID of the product.
 * @property {string} url - The URL of the product.
 * @returns {void} - The function does not return a value.
 */
export async function sendCRMEvent(email, skinConcern, productsCRM, language) {
	try {
		const payload = {
			action: 'send_event',
			email,
			skinConcern,
			productsCRM,
			language
		}

		const response = await fetch(process.env.VUE_APP_FUNCTIONS_API, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(payload)
		})

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const result = await response.json()
		if (!result.success) {
			throw new Error('Failed to send CRM event.')
		}

		return true
	} catch (error) {
		this.$ga.send(this.$constants.ANALYTICS_EVENTS.PRODUCT_FINDER_ERROR, {
			product_finder_name: this.$constants.PRODUCT_FINDER_NAME,
			error_type: 'send_crm_event'
		})
		// @notes: track error
		console.error('Error:', error)
	}
}

/**
 * Sets a cookie with the given name, value and expiry days
 * @param {string} name - The name of the cookie
 * @param {string} value - The value to store in the cookie
 * @param {number} days - Number of days until the cookie expires
 * @param {Object} options - Optional cookie attributes
 * @param {string} options.path - Cookie path (defaults to '/')
 * @param {boolean} options.secure - Whether cookie should only be transmitted over HTTPS
 * @param {boolean} options.httpOnly - Whether cookie should be inaccessible to JavaScript
 */
export function setCookie(name, value, days, options = {}) {
	// Encode value to handle special characters
	const encodedValue = encodeURIComponent(value)

	const d = Date.now() + days * 24 * 60 * 60 * 1000
	// Set expiry date
	const expires = days ? `; expires=${new Date(d).toUTCString()}` : ''

	// Set path (default to root)
	const path = options.path ? options.path : '/'

	// Add secure flag if specified
	const secure = options.secure ? '; secure' : ''

	// Add httpOnly flag if specified
	const httpOnly = options.httpOnly ? '; httpOnly' : ''

	// Set the cookie
	document.cookie = `${name}=${encodedValue}${expires}; path=${path}${secure}${httpOnly}`
}

/**
 * Gets a cookie value by name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string|null} The cookie value if found, null otherwise
 */
export function getCookie(name) {
	// Add = to name for exact match
	const nameEQ = name + '='

	// Get all cookies and decode
	const decodedCookie = decodeURIComponent(document.cookie)
	const cookies = decodedCookie.split(';')

	// Find matching cookie
	for (let i = 0; i < cookies.length; i++) {
		let cookie = cookies[i]

		// Remove whitespace
		while (cookie.charAt(0) === ' ') {
			cookie = cookie.substring(1)
		}

		// Return value if name matches
		if (cookie.indexOf(nameEQ) === 0) {
			return cookie.substring(nameEQ.length)
		}
	}

	return null
}

/**
 * Deletes a cookie by setting its expiry to the past
 * @param {string} name - The name of the cookie to delete
 * @param {string} path - The path of the cookie (must match the path used to set)
 */
export function deleteCookie(name, path = '/') {
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`
}
